import { useMemo } from 'react';
import { Group, NumberInput, Stack, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { PACKAGING_TYPE_OPTIONS } from '../../../../ltl/constants';
import { Dropdown } from '../../../common/Dropdown';

import type { LtlCommodity } from '../../../../types/ltl';

export type CommodityDetailsProps = {
  commodity: LtlCommodity;
  index: number;
  onChange: (next: LtlCommodity) => void;
};

/**
 * The "Show commodity details" reveal block for a commodity card: NMFC code,
 * piece type, number of pieces, and commodity description.
 */
export const CommodityDetails = ({ commodity, index, onChange }: CommodityDetailsProps) => {
  const intl = useIntl();

  const pieceTypeData = useMemo(
    () => PACKAGING_TYPE_OPTIONS.map((option) => ({ value: option.value, label: intl.formatMessage(option.label) })),
    [intl],
  );

  const handleNumberOfPiecesChange = (value: number | string) => {
    const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
    onChange({ ...commodity, numberOfPieces: Number.isNaN(parsed) ? undefined : parsed });
  };

  return (
    <Stack gap='sm'>
      <Group grow>
        <TextInput
          data-testid={`commodity-nmfc-${index}`}
          label={intl.formatMessage(handlingUnitsStepMessages.nmfcCodeLabel)}
          onChange={(event) => onChange({ ...commodity, nmfcCode: event.currentTarget.value })}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.nmfcCodePlaceholder)}
          value={commodity.nmfcCode}
        />
        <Dropdown
          clearable
          data={pieceTypeData}
          data-testid={`commodity-piece-${index}`}
          label={intl.formatMessage(handlingUnitsStepMessages.pieceLabel)}
          onChange={(value) => onChange({ ...commodity, pieceType: value ?? undefined })}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.piecePlaceholder)}
          value={commodity.pieceType ?? null}
        />
        <NumberInput
          allowNegative={false}
          data-testid={`commodity-number-of-pieces-${index}`}
          label={intl.formatMessage(handlingUnitsStepMessages.numberOfPiecesLabel)}
          min={0}
          onChange={handleNumberOfPiecesChange}
          value={commodity.numberOfPieces ?? ''}
        />
      </Group>
      <TextInput
        data-testid={`commodity-description-${index}`}
        label={intl.formatMessage(handlingUnitsStepMessages.commodityDescriptionLabel)}
        onChange={(event) => onChange({ ...commodity, description: event.currentTarget.value })}
        placeholder={intl.formatMessage(handlingUnitsStepMessages.commodityDescriptionPlaceholder)}
        value={commodity.description}
      />
    </Stack>
  );
};
